<script setup lang="ts">
// The machine's detail (DetailPanel). Overview: its state, then what it is
// (specs), what its sensors read now (load, temperatures, fans, drives,
// GPUs), then its DeckLink cards. Health: the problems acknowledged and the
// check before the event. Network: its interfaces and its clock. Graphs.
import { computed, onMounted, onUnmounted, ref } from 'vue'
import {
    api,
    ApiError,
    central,
    cloudNames,
    money,
    nodeId,
    type CloudVM,
    type MachineStatus,
    type NetSettings,
    type RebootPlan,
    type Sample,
    type SystemSettings,
    type SystemStatus,
    virtualConfig,
} from '../../api'
import { useNode } from '../../store'
import { ago, dateTime, hoursText as hours, mbps } from '../../util'
import { hasFeature } from '../../features'
import {
    latestAsk,
    mustType,
    placeText,
    rebootLines,
    rootAction,
    rootRefusal,
    shutdownLines,
    stopKey,
} from '../../reboot'
import { machineProblems } from '../../problems'
import { driveRows, fanRow, temperatureRows } from '../../sensors'
import CoreHeatmap from '../CoreHeatmap.vue'
import Figure from './Figure.vue'
import StatusSlot from './StatusSlot.vue'
import NetEdit from './NetEdit.vue'
import TimeSettings from './TimeSettings.vue'
import EventCheck from './EventCheck.vue'
import UpdateHold from './UpdateHold.vue'
import Sparkline from '../Sparkline.vue'
import ConfirmDialog from '../ConfirmDialog.vue'
import NeedsNewer from '../NeedsNewer.vue'

const node = useNode()
const now = computed(() => node.now)
const pct = (v: number) => `${Math.round(v)}%`
// DeckLink card profiles (decklink-profile.cpp): how connectors form
// devices, per profile group (a whole 8K Pro, each connector pair of a Duo
// 2 or Quad 2)
const profileLabels: Record<string, string> = {
    '4-half': 'four devices, half duplex (each connector its own input or output)',
    '2-full': 'two devices, full duplex',
    '2-half': 'two devices, half duplex (each connector its own input or output)',
    '1-full': 'one device, full duplex (all its connectors together)',
    '1-half': 'one device, half duplex (all its connectors together)',
}
const profileLabel = (p: string) => profileLabels[p] ?? p
// a group's devices, the name said once: "DeckLink Quad (1), (5)"
const groupLabel = (c: { name: string; devices?: string[] }) => {
    const d = c.devices?.length ? c.devices : [c.name]
    const stem = (d[0] ?? c.name).replace(/\s*\(\d+\)$/, '')
    return d.map((n, i) => (i && n.startsWith(stem + ' (') ? n.slice(stem.length + 1) : n)).join(', ')
}
// the clock: synced, and how far off its server
const props = defineProps<{
    tab: string // the detail panel's: overview, health, graphs or network (detailtabs.ts)
    machine: MachineStatus
    series: Sample[]
    times: number[]
    graphFrom: number
    graphLabel: string
}>()
// the public address first (the node's own hourly lookup), then each private one
const addrs = computed(() => [
    ...(props.machine.public_ip ? [{ ip: props.machine.public_ip, label: 'public' }] : []),
    ...(props.machine.addresses ?? [])
        .filter((a) => a !== props.machine.public_ip)
        .map((ip) => ({ ip, label: 'private' })),
])
const clock = computed(() => {
    const c = props.machine.clock
    if (!c) return { text: '—', bad: false }
    if (!c.managed) return { text: 'kept by the host', bad: false }
    if (!c.synced) return { text: 'not synchronised', bad: true }
    const off = c.offset_ms !== undefined ? ` ${c.offset_ms >= 0 ? '+' : ''}${c.offset_ms.toFixed(1)} ms` : ''
    return { text: `NTP${off}${c.server ? ' · ' + c.server : ''}`, bad: Math.abs(c.offset_ms ?? 0) > 100 }
})
// the machine's own settings (/api/system): time servers and the network,
// followed while the panel is open (root applies them, a network change
// waits on trial to be kept)
const sys = ref<SystemStatus | null>(null)
const editing = ref<string | null>(null) // the interface being set
async function loadSystem() {
    try {
        sys.value = await api.system()
    } catch {
        /* an older node */
    }
}
let sysTimer = 0
// the cloud VM central made this node on, with what it costs (superadmins,
// through central)
const vm = ref<CloudVM | null>(null)
async function loadVM() {
    if (!node.central || !node.user?.superadmin || !nodeId) return
    vm.value = (await central.cloudVMs(nodeId).catch(() => []))[0] ?? null
}
let vmTimer = 0
// a reboot of the node (DECISIONS 2026-10-18 "a reboot from the interface")
// or its shutdown (2026-10-18 "a shutdown from the interface"), one plan and
// one dialog: the node's plan, polled while the panel is open so a refusal (an
// update under way, a network change on trial, a reboot just asked) disables
// both buttons; the dialog keeps its own copy, the one the operator is shown
const plan = ref<RebootPlan | null>(null)
const dialog = ref<RebootPlan | null>(null)
const action = ref<'reboot' | 'shutdown'>('reboot') // what the open dialog asks
const dialogError = ref('')
const dialogKey = ref(0) // bumped on every open and when a 409 changes the plan: the dialog opens afresh, the name to be typed again
const asking = ref(false)
// a dialog being opened: both buttons are off meanwhile, and only the newest
// open may show or change the dialog (a late open of an earlier click must
// not turn a typed reboot into a shutdown, or the reverse)
const opening = ref(false)
const opens = latestAsk()
const canReboot = computed(() => !node.readOnly && virtualConfig === null)
const nodeName = computed(() => node.info?.name || node.config?.node.name || 'this node')
// the node's own refusal, else root's if it is fresh (its file stays for days)
const refusal = computed(() => plan.value?.refused || '')
const rootSaid = computed(() => (plan.value ? rootRefusal(plan.value, now.value) : ''))
// ponytail: the node's own page knows no partner, so no warning there; central's only.
const partner = computed(() => (node.pair ? { name: node.partner, online: node.partnerOnline } : null))
// the open dialog's lines: the shutdown has a warning (and its partner line), the reboot an update hold
const lines = computed(() => {
    if (!dialog.value) return null
    if (action.value === 'reboot') return { warning: '', ...rebootLines(dialog.value, partner.value) }
    // the shutdown says where the node is and what it runs on, so the dialog tells who turns it on again
    const c = props.machine.specs?.cloud
    return {
        hold: '',
        ...shutdownLines(
            dialog.value,
            partner.value,
            c ? (cloudNames[c.provider] ?? c.provider) : null,
            placeText(node.info),
        ),
    }
})
async function loadPlan() {
    if (!canReboot.value || !hasFeature(node.features, 'reboot') || dialog.value) return
    try {
        plan.value = await api.rebootPlan()
    } catch {
        /* an older node, or none for now: the click says */
    }
}
let planTimer = 0
async function openDialog(a: 'reboot' | 'shutdown') {
    if (opening.value) return
    const current = opens.begin()
    opening.value = true
    dialogError.value = ''
    try {
        const p = await api.rebootPlan()
        if (!current()) return
        plan.value = p
        if (p.refused) return // the button is off, with the text
        if (node.central) {
            const ns = await central.nodes() // is the partner up now
            if (!current()) return
            node.pairFrom(ns)
        }
        action.value = a
        dialog.value = p
        dialogKey.value++
    } catch (e) {
        if (current()) node.notify(e instanceof Error ? e.message : String(e), true)
    } finally {
        if (current()) opening.value = false
    }
}
async function confirmReboot() {
    if (!dialog.value || asking.value) return
    asking.value = true
    try {
        const stops = dialog.value.stops.map(stopKey)
        if (action.value === 'shutdown') {
            await api.shutdown(stops)
            dialog.value = null
            node.notify(
                'Shutdown asked: the node goes offline in a few seconds and stays off until someone powers it on',
            )
        } else {
            await api.reboot(stops)
            dialog.value = null
            node.notify('Reboot asked: the node goes offline in a few seconds')
        }
        void loadPlan()
    } catch (e) {
        dialogError.value = e instanceof Error ? e.message : String(e)
        if (e instanceof ApiError && e.status === 409) {
            // something started since the dialog, or the node refuses now: the new plan, to confirm again
            try {
                const p = await api.rebootPlan()
                plan.value = p
                if (p.refused) dialog.value = null
                else {
                    dialog.value = p
                    dialogKey.value++
                }
            } catch {
                /* the error above stays */
            }
            if (!dialog.value) node.notify(dialogError.value, true)
        }
    } finally {
        asking.value = false
    }
}
onMounted(() => {
    void loadPlan()
    planTimer = window.setInterval(loadPlan, 10_000)
    void loadSystem()
    sysTimer = window.setInterval(loadSystem, 3000)
    void loadVM()
    vmTimer = window.setInterval(loadVM, 60_000)
})
onUnmounted(() => {
    clearInterval(sysTimer)
    clearInterval(vmTimer)
    clearInterval(planTimer)
})
async function putSystem(s: SystemSettings, done: string) {
    try {
        sys.value = await api.putSystem(s)
        node.notify(done)
        return true
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
        return false
    }
}
function saveNTP(ntp: string[]) {
    if (!sys.value) return
    void putSystem(
        { ...sys.value.settings, ntp },
        ntp.length ? 'Time servers saved; the node applies them now' : "Back to the system's own time servers",
    )
}
const netOf = (name: string) => sys.value?.settings.network.find((n) => n.interface === name)
async function saveNet(name: string, s: NetSettings | null) {
    if (!sys.value) return
    const network = sys.value.settings.network.filter((n) => n.interface !== name)
    if (s) network.push(s)
    network.sort((a, b) => a.interface.localeCompare(b.interface))
    if (await putSystem({ ...sys.value.settings, network }, `${name}: applying; keep the settings within two minutes`))
        editing.value = null
}
const trialLeft = computed(() =>
    sys.value?.pending ? Math.max(0, Math.round((Date.parse(sys.value.pending) - now.value) / 1000)) : 0,
)
async function keepNetwork() {
    try {
        sys.value = await api.confirmSystem()
        node.notify('Network settings kept')
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    }
}
const netLabel = (s?: NetSettings) =>
    !s ? 'system default' : s.dhcp ? 'DHCP' : `static${s.gateway ? ' via ' + s.gateway : ''}`
// a link's speed: 100 Mb/s, 1 Gb/s, 2.5 Gb/s
const linkRate = (mbps: number) => (mbps < 1000 ? `${mbps} Mb/s` : `${mbps / 1000} Gb/s`)
// the problems the state stands on (faults first, in red) and those
// acknowledged on the node, dimmed until they lapse (problems.ts)
const shown = computed(() => machineProblems(props.machine, node.config?.machine_acks))
const problems = computed(() => shown.value.open)
const acked = computed(() => shown.value.acked)
const canAck = computed(() => !node.readOnly && problems.value.some((p) => p.key))
const noting = ref<string | null>(null) // the problem whose acknowledgement is being written
const note = ref('')
const acking = ref(false)
async function ack(key: string, why: string | null) {
    acking.value = true
    try {
        await node.ackProblem(key, why)
        noting.value = null
        note.value = ''
    } catch {
        /* toast shown by the store */
    } finally {
        acking.value = false
    }
}
// the sensors, a row each (sensors.ts); a GPU's temperature is with its figures
const sensorRows = computed(() => [
    ...temperatureRows(props.machine.temps ?? []),
    ...fanRow(props.machine.fans ?? []),
    ...driveRows(props.machine.drives ?? []),
])
const toneOf: Record<string, string> = { warn: 'text-warn', bad: 'text-bad', dim: 'text-fg-2', '': '' }
// the slot's message: one problem a line, what then what to do
const problemText = computed(() => problems.value.map((p) => p.what + (p.hint ? ' — ' + p.hint : '')).join('\n'))
async function measureNVENC() {
    try {
        await api.nvencProbe()
        node.notify('Measuring the NVENC session limit (about half a minute)')
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    }
}
</script>

<template>
    <template v-if="tab === 'overview'">
        <!-- the state and its problems in a slot of one height: a problem
             coming and going moves nothing under the pointer -->
        <StatusSlot
            :tone="problems[0]?.fault ? 'bad' : problems.length ? 'warn' : 'ok'"
            :label="problems.length > 1 ? `${problems.length} problems` : 'problem'"
            :message="problemText"
            :idle="acked.length ? `no problems · ${acked.length} acknowledged, on Health` : undefined"
            data-test="machine-problems"
        >
            <span class="font-medium">{{ machine.state.replace('_', ' ') }}</span>
            <span class="text-fg-2 tabular-nums">for {{ ago(machine.since, now) }}</span>
            <template v-if="canAck" #more>
                <div v-if="canAck" class="mt-2 border-t border-line pt-2" data-test="machine-ack">
                    <div class="mb-1 text-[11px] text-fg-3">
                        Known and harmless here? Acknowledge it: the machine's state stops standing on it, until it has
                        been gone for 10 minutes.
                    </div>
                    <div v-for="p in problems.filter((q) => q.key)" :key="p.key" class="flex items-center gap-2 py-0.5">
                        <span
                            class="min-w-0 flex-1 truncate"
                            :class="p.fault ? 'text-bad' : 'text-warn'"
                            :title="p.what"
                            >{{ p.what }}</span
                        >
                        <template v-if="noting === p.key">
                            <input
                                v-model="note"
                                class="w-40 text-xs"
                                maxlength="300"
                                placeholder="why (optional)"
                                data-test="ack-note"
                                @keydown.enter="ack(p.key, note)"
                            />
                            <button
                                class="text-xs"
                                :disabled="acking"
                                data-test="ack-confirm"
                                @click="ack(p.key, note)"
                            >
                                acknowledge
                            </button>
                        </template>
                        <button v-else class="text-xs" data-test="ack" @click="((noting = p.key), (note = ''))">
                            acknowledge…
                        </button>
                    </div>
                </div>
            </template>
        </StatusSlot>
        <!-- why a reboot is wanted stays in sight; its button is under Power, at the end -->
        <div v-if="machine.reboot_reason" class="mt-2 text-xs text-warn" data-test="reboot-reason">
            Reboot needed: {{ machine.reboot_reason }}<template v-if="canReboot"> · Power, below</template>
        </div>
        <ConfirmDialog
            v-if="dialog && lines"
            :key="dialogKey"
            :title="action === 'shutdown' ? `Shut down ${nodeName}?` : `Reboot ${nodeName}?`"
            :body="
                action === 'shutdown'
                    ? `The node goes offline and does not come back by itself.${Date.parse(dialog.up_since) > 0 ? ' Up since ' + dateTime(dialog.up_since) + '.' : ''}`
                    : `The node goes offline for a few minutes.${Date.parse(dialog.up_since) > 0 ? ' Up since ' + dateTime(dialog.up_since) + '.' : ''}${machine.reboot_reason ? ' A reboot is wanted: ' + machine.reboot_reason + '.' : ''}`
            "
            :typed="action === 'shutdown' || mustType(dialog) ? nodeName : undefined"
            :confirm-label="action === 'shutdown' ? 'Shut down' : 'Reboot'"
            danger
            @confirm="confirmReboot"
            @cancel="dialog = null"
        >
            <p v-if="lines.warning" class="mt-0 mb-3 text-[13px] font-semibold text-bad" data-test="shutdown-warning">
                {{ lines.warning }}
            </p>
            <ul
                v-if="lines.stops.length || lines.plain.length || lines.partner || lines.hold"
                class="mt-0 mb-3.5 pl-4 text-xs"
            >
                <li v-for="l in lines.stops" :key="l" class="font-medium text-bad" data-test="reboot-stop">
                    {{ l }}
                </li>
                <li v-for="l in lines.plain" :key="l" class="text-fg-2" data-test="reboot-plain">{{ l }}</li>
                <li v-if="lines.partner" class="text-warn" data-test="reboot-partner">{{ lines.partner }}</li>
                <li v-if="lines.hold" class="text-warn" data-test="reboot-hold">{{ lines.hold }}</li>
            </ul>
            <p v-if="dialogError" class="mt-0 mb-3.5 text-xs text-bad" role="alert" data-test="reboot-error">
                {{ dialogError }}
            </p>
        </ConfirmDialog>
        <!-- what the machine is, then what its sensors read now, then the rest -->
        <!-- the node's updates held for an event: a node from before the hold shows none -->
        <UpdateHold v-if="virtualConfig === null && hasFeature(node.features, 'update_hold')" class="mt-3" />
        <div class="mt-4 mb-1.5 subhead">Specs</div>
        <div class="grid grid-cols-3 gap-x-3.5 gap-y-2.5">
            <div data-test="machine-host">
                <div class="text-[11px] text-fg-3">hostname</div>
                <div class="truncate font-mono text-[13px]" :title="machine.hostname">
                    {{ machine.hostname || '—' }}
                </div>
            </div>
            <div class="col-span-2" data-test="machine-addresses">
                <div class="text-[11px] text-fg-3">addresses</div>
                <div class="flex flex-wrap gap-x-2.5">
                    <button
                        v-for="a in addrs"
                        :key="a.ip"
                        class="btn-ghost p-0 font-mono text-[13px]"
                        :title="`Copy ${a.ip}`"
                        @click="node.copy(a.ip)"
                    >
                        {{ a.ip }} <span class="font-sans text-[11px] text-fg-3">{{ a.label }}</span>
                    </button>
                    <span v-if="!addrs.length" class="text-[13px] text-fg-2">—</span>
                </div>
            </div>
            <div>
                <div class="text-[11px] text-fg-3">cpu</div>
                <div class="text-[13px]">
                    {{ machine.specs.cpu_model || '—' }}
                    <span class="text-fg-2">· {{ machine.specs.cores }} threads</span>
                </div>
            </div>
            <div>
                <div class="text-[11px] text-fg-3">memory</div>
                <div class="text-[13px]">{{ machine.specs && machine.mem_total_gb.toFixed(0) }} GB</div>
                <div v-if="machine.memory_modules?.length" class="text-[12px] text-fg-2" data-test="memory-modules">
                    {{ machine.memory_modules.length }} × {{ machine.memory_modules[0]!.size_gb }} GB
                    {{ machine.memory_modules[0]!.type }}
                    {{ machine.memory_modules[0]!.configured_mts || machine.memory_modules[0]!.speed_mts }}
                    MT/s<template v-if="machine.memory_ecc">
                        · {{ machine.memory_ecc === 'None' ? 'no ECC' : machine.memory_ecc }}</template
                    >
                </div>
            </div>
            <div>
                <div class="text-[11px] text-fg-3">os</div>
                <div class="text-[13px]">
                    {{ machine.specs.os || '—' }}
                    <span class="text-fg-2">· {{ machine.specs.kernel }}</span>
                </div>
            </div>
            <div>
                <div class="text-[11px] text-fg-3">ffmpeg</div>
                <div class="font-mono text-[13px] tabular-nums">{{ machine.specs.ffmpeg || '—' }}</div>
            </div>
            <div v-for="g in machine.gpus" :key="g.index">
                <div class="text-[11px] text-fg-3">gpu{{ g.index }}</div>
                <div class="truncate text-[13px]" :title="g.name">
                    {{ g.name }} <span class="text-fg-2">· driver {{ g.driver }}</span>
                </div>
            </div>
            <div v-if="!machine.gpus.length">
                <div class="text-[11px] text-fg-3">gpu</div>
                <div class="text-[13px] text-fg-2" :class="machine.gpu_note ? 'text-warn' : ''">
                    {{ machine.gpu_note || 'none detected' }}
                </div>
            </div>
            <div v-if="machine.specs.cloud" data-test="cloud-spec">
                <div class="text-[11px] text-fg-3">cloud</div>
                <div class="text-[13px]">
                    {{ cloudNames[machine.specs.cloud.provider] ?? machine.specs.cloud.provider }}
                    <span v-if="machine.specs.cloud.zone" class="text-fg-2">· {{ machine.specs.cloud.zone }}</span>
                </div>
                <div v-if="machine.specs.cloud.machine_type" class="font-mono text-[12px] text-fg-2">
                    {{ machine.specs.cloud.machine_type }}
                </div>
            </div>
            <div
                v-if="vm"
                data-test="cloud-cost"
                title="At list prices, egress included: an estimate, not the cloud's bill"
            >
                <div class="text-[11px] text-fg-3">cloud cost</div>
                <div class="text-[13px]">
                    <template v-if="vm.state === 'running'">
                        this run {{ hours(vm.cost.run_hours) }}:
                        <b>{{ money(vm.cost.run_cost, vm.cost.currency) }}</b>
                        <span class="text-fg-2">· {{ money(vm.cost.hourly, vm.cost.currency) }}/h</span>
                    </template>
                    <template v-else>{{ vm.state }}</template>
                    <span
                        v-if="vm.static_ip"
                        class="ml-1 rounded-[3px] border border-accent px-1 text-[10px] text-accent"
                        title="Reserved: the address stays across stops"
                        >static IP</span
                    >
                </div>
                <div class="text-[12px] text-fg-2">
                    total {{ money(vm.cost.total_cost, vm.cost.currency) }} since
                    {{ ago(vm.created_at, node.now) }} ago<template v-if="vm.cost.total_egress_gb">
                        · {{ vm.cost.total_egress_gb.toFixed(1) }} GB out</template
                    >
                </div>
            </div>
        </div>
        <div class="mt-4 mb-1.5 subhead">Sensors</div>
        <div class="grid grid-cols-3 gap-x-3.5 gap-y-2.5">
            <Figure label="cpu">{{ pct(machine.cpu_pct) }}</Figure>
            <Figure label="memory"
                >{{ pct(machine.mem_pct)
                }}<template #sub
                    >{{ machine.mem_used_gb.toFixed(1) }} of {{ machine.mem_total_gb.toFixed(0) }} GB</template
                ></Figure
            >
            <Figure label="network"
                >↓ {{ mbps(machine.net_rx_kbps) }}<template #sub>↑ {{ mbps(machine.net_tx_kbps) }}</template></Figure
            >
            <Figure label="clock" :full="clock.text"
                ><span class="font-sans" :class="clock.bad ? 'text-warn' : ''">{{ clock.text }}</span></Figure
            >
            <Figure v-for="d in machine.disks" :key="d.path" :label="`disk ${d.path}`"
                >{{ pct(d.used_pct) }} used<template #sub
                    >{{ (d.total_gb - d.free_gb).toFixed(0) }} of {{ d.total_gb.toFixed(0) }} GB</template
                ></Figure
            >
        </div>
        <!-- a row per chip, the fans, a row per drive (sensors.ts) -->
        <div class="mt-1 rounded-md border border-line text-xs" data-test="machine-sensors">
            <div
                v-for="(r, i) in sensorRows"
                :key="r.name"
                class="grid grid-cols-[5.5rem_minmax(0,1fr)] items-baseline gap-x-2 px-2.5 py-1"
                :class="{ 'border-t border-line': i > 0 }"
            >
                <span class="truncate text-[11px] text-fg-3" :title="r.name">{{ r.name }}</span>
                <span class="flex flex-wrap gap-x-3 gap-y-0.5">
                    <span v-for="(x, j) in r.readings" :key="j" class="whitespace-nowrap" :title="x.title"
                        ><span v-if="x.label" class="mr-1 text-fg-2">{{ x.label }}</span
                        ><span class="tabular-nums" :class="toneOf[x.tone ?? '']">{{ x.value }}</span></span
                    >
                </span>
            </div>
            <div v-if="!sensorRows.length" class="px-2.5 py-1 text-fg-2">
                No temperature, fan or drive sensors readable here (a container or a VM).
            </div>
        </div>
        <div v-for="g in machine.gpus" :key="g.index" class="mt-2.5" data-test="gpu-figures">
            <div class="text-[11px] text-fg-3">
                gpu{{ g.index
                }}<span v-if="g.power_w" class="text-fg-2" data-test="gpu-power">
                    · {{ g.power_w.toFixed(0)
                    }}<template v-if="g.power_limit_w"> / {{ g.power_limit_w.toFixed(0) }}</template> W</span
                ><span v-if="g.ecc_corrected !== undefined" class="text-fg-2">
                    · ECC {{ g.ecc_uncorrected ?? 0 }} uncorrected, {{ g.ecc_corrected }} corrected</span
                >
            </div>
            <!-- why its clocks are held down now; the power cap is normal at full load -->
            <div v-if="g.throttle?.length" class="flex flex-wrap gap-1 py-0.5" data-test="gpu-throttle">
                <span
                    v-for="t in g.throttle"
                    :key="t"
                    class="rounded-[3px] border px-1 text-[10px]"
                    :class="t === 'power cap' ? 'border-line text-fg-2' : 'border-warn text-warn'"
                    >{{ t }}</span
                >
            </div>
            <div class="grid grid-cols-4 gap-x-3.5">
                <Figure label="util">{{ pct(g.util_pct) }}</Figure>
                <Figure label="encoder">{{ pct(g.enc_pct) }}</Figure>
                <Figure label="memory">{{ pct(g.mem_pct) }}</Figure>
                <Figure label="temperature">{{ g.temp_c }} °C</Figure>
            </div>
            <div class="text-xs">
                NVENC sessions
                <b class="font-mono tabular-nums"
                    >{{ g.sessions
                    }}<template v-if="g.nvenc_max"> / {{ g.nvenc_max }}{{ g.nvenc_no_limit ? '+' : '' }}</template></b
                >
                <span class="text-fg-2"
                    ><template v-if="g.nvenc_max">
                        ·
                        {{
                            g.nvenc_no_limit ? 'no driver limit found' : 'the driver allows ' + g.nvenc_max + ' at once'
                        }}</template
                    ><template v-if="g.nvenc_note"> · {{ g.nvenc_note }}</template></span
                >
                <button
                    class="btn-ghost text-xs"
                    :title="
                        g.sessions
                            ? 'Measure now: running encodes count; one starting meanwhile can be refused once'
                            : 'Measure the session limit again'
                    "
                    @click="measureNVENC"
                >
                    measure
                </button>
            </div>
        </div>
        <div
            v-if="machine.kernel_note || machine.drive_note || machine.notes?.length"
            class="mt-2 text-xs text-fg-3"
            data-test="kernel-note"
        >
            <div v-if="machine.kernel_note">{{ machine.kernel_note }}</div>
            <div v-if="machine.drive_note">{{ machine.drive_note }}</div>
            <div v-for="(n, i) in machine.notes ?? []" :key="i" data-test="machine-note">{{ n }}</div>
        </div>
        <div class="mt-4 mb-1.5 subhead">DeckLink</div>
        <div v-if="!machine.decklink.length" class="text-xs text-fg-2">
            {{
                machine.decklink_note ||
                'No DeckLink devices detected (no card, no driver, or ffmpeg built without decklink).'
            }}
        </div>
        <div v-for="d in machine.decklink" :key="d.name" class="flex items-center gap-2 px-0 py-0.5 text-xs">
            <span class="font-mono tabular-nums">{{ d.name }}</span
            ><span class="text-fg-2">{{ [d.input && 'input', d.output && 'output'].filter(Boolean).join(' · ') }}</span>
        </div>
        <div
            v-for="c in machine.decklink_cards ?? []"
            :key="c.name"
            class="px-0 py-0.5 text-xs text-fg-2"
            :title="`offers ${c.profiles.join(', ')}; set at boot, see install.sh --decklink-profile`"
        >
            profile of {{ groupLabel(c) }}: {{ profileLabel(c.profile) }}
        </div>
        <div
            v-for="d in machine.decklink_ip ?? []"
            :key="'ip-' + d.device"
            class="mt-1.5 text-xs"
            data-test="decklink-ip"
        >
            <div>
                <span class="font-mono">{{ d.device }}</span> <span class="text-fg-2">· SMPTE 2110</span>
                <span :class="d.ptp.grandmaster ? 'text-fg-2' : 'text-warn'">
                    · PTP {{ d.ptp.grandmaster ? 'locked to ' + d.ptp.grandmaster : 'not locked' }} (domain
                    {{ d.ptp.domain }})</span
                ><span class="text-fg-2"> · NMOS {{ d.nmos_registry || 'no registry' }}</span>
            </div>
            <div v-for="e in d.ethernet" :key="e.index" class="pl-3 text-fg-2">
                port {{ e.index + 1 }}:
                <span :class="e.link === 'bound' ? '' : 'text-warn'">{{
                    e.link === 'bound' ? `${e.address} (${e.dhcp ? 'DHCP' : 'static'})` : e.link
                }}</span>
                <template v-if="e.mbps"> · {{ e.mbps / 1000 }} Gb/s</template>
                <span v-if="e.rx_dropped" class="text-warn">
                    · {{ e.rx_dropped.toLocaleString() }} of {{ e.rx_packets.toLocaleString() }} packets dropped</span
                >
            </div>
            <div v-for="f in d.flows" :key="f.id" class="pl-3 text-fg-2">
                flow {{ f.id }}: {{ f.type }} {{ f.direction === 'in' ? 'receiver' : 'sender' }}
                <details v-if="f.sdp" class="inline">
                    <summary class="inline cursor-pointer">its SDP</summary>
                    <pre class="font-mono text-[11px] whitespace-pre-wrap select-all">{{ f.sdp }}</pre>
                </details>
            </div>
        </div>
        <!-- a reboot and a shutdown, last: away from what is pressed in the
             moment (Niels); a viewer never sees them; an older node says why they are off -->
        <template v-if="canReboot">
            <div class="mt-4 mb-1.5 subhead">Power</div>
            <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-test="reboot">
                <template v-if="hasFeature(node.features, 'reboot')">
                    <span v-if="plan?.available === false" class="text-fg-2" data-test="reboot-unavailable">{{
                        plan.refused
                    }}</span>
                    <template v-else>
                        <button
                            :class="{ 'btn-danger': machine.reboot_reason }"
                            :disabled="!!refusal || opening"
                            :title="refusal || 'Reboot this node'"
                            data-test="reboot-button"
                            @click="openDialog('reboot')"
                        >
                            Reboot
                        </button>
                        <button
                            v-if="hasFeature(node.features, 'shutdown')"
                            class="btn-danger-solid"
                            :disabled="!!refusal || opening"
                            :title="refusal || 'Shut this node down: it stays off until someone powers it on'"
                            data-test="shutdown-button"
                            @click="openDialog('shutdown')"
                        >
                            Shut down
                        </button>
                        <NeedsNewer v-else feature="shutdown" />
                        <span v-if="refusal" class="text-fg-2" data-test="reboot-refused">{{ refusal }}</span>
                        <span v-else-if="rootSaid" class="text-warn" data-test="reboot-root"
                            >The last {{ plan ? rootAction(plan) : 'reboot' }} was refused: {{ rootSaid }}</span
                        >
                    </template>
                </template>
                <NeedsNewer v-else feature="reboot" />
            </div>
        </template>
    </template>
    <template v-if="tab === 'health'">
        <!-- the problems acknowledged first: a box that changes only when one
         is, above the check whose steps come and go -->
        <div class="mb-3 rounded-md border border-line text-xs" data-test="machine-acked">
            <div class="border-b border-line bg-bg-2 px-2.5 py-1.5 subhead">Acknowledged problems</div>
            <div class="px-2.5 py-2">
                <div class="mb-1 text-[11px] text-fg-3">
                    Still there, but the machine's state no longer stands on them; each lapses once its problem has been
                    gone for 10 minutes. Acknowledge one from the machine's state on Overview.
                </div>
                <div v-if="!acked.length" class="text-fg-2">None.</div>
                <div v-for="p in acked" :key="p.key" class="flex items-baseline gap-2 py-0.5">
                    <span class="min-w-0 flex-1" :title="p.text"
                        ><span v-if="p.fault" class="mr-1 text-bad">fault</span>{{ p.what
                        }}<span v-if="p.ack" class="text-fg-3">
                            · {{ p.ack.by }}, {{ ago(p.ack.at, now) }} ago<template v-if="p.ack.note">
                                · {{ p.ack.note }}</template
                            ></span
                        ></span
                    >
                    <button
                        v-if="!node.readOnly"
                        class="text-xs"
                        :disabled="acking"
                        data-test="unack"
                        @click="ack(p.key, null)"
                    >
                        withdraw
                    </button>
                </div>
            </div>
        </div>
        <EventCheck v-if="virtualConfig === null" />
    </template>
    <template v-if="tab === 'network'">
        <div
            v-if="sys?.pending"
            class="mb-1.5 flex flex-wrap items-center gap-2 rounded-md border border-warn px-2.5 py-1.5 text-xs"
            role="alert"
        >
            <span class="min-w-0 flex-1"
                >New network settings are on trial: the node goes back to the previous ones in
                <b class="tabular-nums">{{ trialLeft }} s</b> unless they are kept.</span
            >
            <button class="btn-primary text-xs" @click="keepNetwork">Keep these settings</button>
        </div>
        <div class="mb-1.5 subhead">Interfaces</div>
        <div class="rounded-md border border-line text-xs">
            <template v-for="(i, n) in machine.interfaces ?? []" :key="i.name">
                <div
                    class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-0.5 px-2.5 py-1.5"
                    :class="{ 'border-t border-line': n > 0 }"
                >
                    <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span class="flex items-center gap-1.5 font-mono tabular-nums" :class="i.up ? '' : 'text-fg-3'"
                            ><span
                                class="inline-block h-2 w-2 rounded-full"
                                :class="i.up ? 'bg-ok' : 'bg-fg-3'"
                                :title="i.up ? 'linked' : 'no link (no cable, or switched off)'"
                            />{{ i.name }}</span
                        >
                        <span
                            v-if="i.speed_mbps"
                            class="tabular-nums"
                            :class="i.speed_mbps < 1000 ? 'text-warn' : 'text-fg-2'"
                            :title="
                                i.speed_mbps < 1000
                                    ? 'under 1 Gb/s: a cable with a broken pair, or the switch port'
                                    : 'link speed'
                            "
                            >{{ linkRate(i.speed_mbps) }}</span
                        >
                        <span class="font-mono text-fg-2 tabular-nums">{{
                            i.addresses.join(' · ') || 'no address'
                        }}</span>
                        <button
                            class="cursor-pointer rounded-[3px] border border-line bg-bg-2 px-1.25 py-0 font-mono text-[11px] text-fg-3 hover:border-line-strong hover:text-fg"
                            :title="`Copy ${i.mac}`"
                            @click="node.copy(i.mac)"
                        >
                            {{ i.mac }}
                        </button>
                    </div>
                    <div v-if="sys" class="flex items-center gap-1.5">
                        <span class="rounded bg-bg-3 px-1.5 py-0.25 text-fg-2">{{ netLabel(netOf(i.name)) }}</span>
                        <button
                            v-if="editing !== i.name"
                            class="btn-ghost px-1.5 py-0 text-xs"
                            :aria-label="`${i.name} settings`"
                            @click="editing = i.name"
                        >
                            Edit
                        </button>
                    </div>
                </div>
                <div v-if="editing === i.name" class="px-2.5 pb-1.5">
                    <NetEdit
                        :iface="i.name"
                        :current="netOf(i.name)"
                        :live="i.addresses"
                        @save="(s) => saveNet(i.name, s)"
                        @cancel="editing = null"
                    />
                </div>
            </template>
        </div>
    </template>
    <div v-if="tab === 'network' && sys?.error" class="mt-1 text-xs text-bad">
        The last change was not applied: {{ sys.error }}
    </div>
    <template v-if="tab === 'network' && sys">
        <div class="mt-4 mb-1.5 subhead">Time</div>
        <TimeSettings :clock="machine.clock" :servers="sys.settings.ntp" @save="saveNTP" />
    </template>
    <div v-if="tab === 'graphs' && series.length < 2" class="text-xs text-fg-2">No samples in this window yet.</div>
    <div v-if="tab === 'graphs' && series.length > 1">
        <Sparkline
            :label="`cpu · ${graphLabel}`"
            :times="times"
            :from="graphFrom"
            :to="node.span[1]"
            :values="series.map((s) => s.cpu ?? 0)"
            :format="pct"
            :max="100"
        />
        <div
            v-if="machine.core_pct?.length"
            class="mt-1 grid grid-cols-[repeat(auto-fill,minmax(30px,1fr))] gap-0.5"
            :title="`${machine.core_pct.length} threads · % busy now`"
        >
            <div
                v-for="(c, i) in machine.core_pct"
                :key="i"
                class="relative h-4 overflow-hidden rounded-[2px] bg-bg-2"
                :title="`thread ${i}`"
            >
                <div
                    class="absolute inset-y-0 left-0 transition-[width] duration-300 ease-linear"
                    :class="
                        c >= 80
                            ? 'bg-[color-mix(in_srgb,var(--warn)_60%,var(--bg-2))]'
                            : 'bg-[color-mix(in_srgb,var(--accent)_45%,var(--bg-2))]'
                    "
                    :style="{ width: Math.max(0, c) + '%' }"
                />
                <span
                    class="absolute inset-0 flex items-center justify-center font-mono text-[10px] text-fg tabular-nums"
                    >{{ c.toFixed(0) }}</span
                >
            </div>
        </div>
        <CoreHeatmap
            v-if="series.some((s) => s.cores?.length)"
            :times="times"
            :cores="series.map((s) => s.cores ?? [])"
            :from="graphFrom"
            :to="node.span[1]"
        />
        <Sparkline
            label="memory"
            :times="times"
            :from="graphFrom"
            :to="node.span[1]"
            :values="series.map((s) => s.mem ?? 0)"
            :format="pct"
            :max="100"
            color="var(--ok)"
        />
        <Sparkline
            v-if="machine.gpus.length"
            label="gpu"
            :times="times"
            :from="graphFrom"
            :to="node.span[1]"
            :values="series.map((s) => s.gpu ?? 0)"
            :format="pct"
            :max="100"
            color="var(--warn)"
        />
        <Sparkline
            v-if="series.some((s) => s.temp_c)"
            label="cpu temperature"
            :times="times"
            :from="graphFrom"
            :to="node.span[1]"
            :values="series.map((s) => s.temp_c ?? 0)"
            :max="120"
            :format="(v) => v.toFixed(0) + ' °C'"
            color="var(--bad)"
        />
        <Sparkline
            v-if="series.some((s) => s.gpu_temp_c)"
            label="gpu temperature"
            :times="times"
            :from="graphFrom"
            :to="node.span[1]"
            :values="series.map((s) => s.gpu_temp_c ?? 0)"
            :max="120"
            :format="(v) => v.toFixed(0) + ' °C'"
            color="var(--bad)"
        />
        <Sparkline
            label="network out"
            :times="times"
            :from="graphFrom"
            :to="node.span[1]"
            :values="series.map((s) => s.bitrate_k ?? 0)"
            :format="mbps"
        />
    </div>
</template>
